import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/buttons';
import { FormCheckbox, FormHelperText, FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Modal, ModalSize } from 'components/notifications';
import {
    buildFactValue,
    buildTachoDebriefErrors,
    findTachoItemField,
    tachoDebriefTextMaxLength
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';

const defaultObject = {};

// Outside a table a missing cost is left out rather than shown as a dash.
function hasGfpCost(item) {
    const value = findTachoItemField(item, "GfpCost")?.value;
    return value != null && value !== "";
}

// The dialog a manager records an infringement's reason for fault and action taken in. It is open
// while a debrief is given, and validates it on every change against the rules the API enforces,
// so each field's message and the save button track the values.
export function TachoDebriefDialog(
    {
        item = null,
        debrief = null,
        saveText = "Debrief",
        allowRemove = false,
        onChange,
        onSave,
        onCancel,
        onRemove = null
    }
) {

    const debriefErrors = debrief == null ? defaultObject : buildTachoDebriefErrors(debrief);
    const debriefIsValid = Object.keys(debriefErrors).length === 0;

    function onTextChange(fieldName, event) {
        onChange(fieldName, event.target.value.slice(0, tachoDebriefTextMaxLength));
    }

    function onSaveClick() {
        if (!debriefIsValid) {
            return;
        }

        onSave();
    }

    return (
        <Modal
            title="Debrief infringement"
            isOpen={debrief != null}
            onClose={onCancel}
            size={ModalSize.MEDIUM}
            updateStateOnClose={false}
            clickOutsideClose={false}
            footer={(
                <div className="bp6-dialog-footer-actions">
                    <Button
                        id="tacho-infringement-debrief-cancel"
                        text="Cancel"
                        intent="secondary"
                        onClick={onCancel}
                    />
                    <ShowHide
                        evaluator={allowRemove && onRemove != null}
                        show={(
                            <Button
                                id="tacho-infringement-debrief-remove"
                                text="Remove"
                                intent="danger"
                                onClick={onRemove}
                            />
                        )}
                    />
                    <Button
                        id="tacho-infringement-debrief-save"
                        text={saveText}
                        intent="primary"
                        disabled={!debriefIsValid}
                        onClick={onSaveClick}
                    />
                </div>
            )}
        >
            <h3>{buildFactValue(item, "InfringementType")}</h3>
            <FormHelperText>
                {buildFactValue(item, "Code")} · {buildFactValue(item, "OccurredAt")}
                <ShowHide
                    evaluator={hasGfpCost(item)}
                    show={(
                        <Fragment> · GFP cost {buildFactValue(item, "GfpCost")}</Fragment>
                    )}
                />
            </FormHelperText>
            <p className="spacer-bottom">{buildFactValue(item, "Description")}</p>
            <FormTextArea
                id="tacho-infringement-reason-for-fault"
                headingText="Reason for fault"
                placeholder="Add the reason for the fault..."
                value={debrief?.reasonForFault ?? ""}
                maxLength={tachoDebriefTextMaxLength}
                showCounter
                fill
                dangerHelperText={debriefErrors.reasonForFault}
                onChange={(event) => onTextChange("reasonForFault", event)}
            />
            <FormTextArea
                id="tacho-infringement-action-taken"
                headingText="Action taken"
                placeholder="Add action taken and notes..."
                value={debrief?.actionTaken ?? ""}
                maxLength={tachoDebriefTextMaxLength}
                showCounter
                fill
                dangerHelperText={debriefErrors.actionTaken}
                onChange={(event) => onTextChange("actionTaken", event)}
            />
            <FormCheckbox
                id="tacho-infringement-emergency"
                label="Emergency"
                checked={debrief?.emergency === true}
                onChange={(event) => onChange("emergency", event.target.checked)}
            />
            <FormHelperText>Tick this if the infringement was a result of an emergency incident.</FormHelperText>
        </Modal>
    );
}

TachoDebriefDialog.propTypes = {
    item: PropTypes.object,
    debrief: PropTypes.object,
    saveText: PropTypes.string,
    allowRemove: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onRemove: PropTypes.func
};
